import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import DebugModal from '~/components/DebugModal/DebugModal';

import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { ListVoucherLogsQuery, ListVoucherLogsResponse } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import CopyableUserLink from '../Common/CopyableUserLink';

type VoucherLog = ListVoucherLogsResponse['result']['logs'][number];

type Props = {
  voucherId?: string;
  queryOptions?: ListVoucherLogsQuery;
  onQueryOptionsChange?: (queryOptions: ListVoucherLogsQuery) => void;
  onUserIdClick?: (userId: string) => void;
  onVoucherClick?: (voucherName: string) => void;
  pageSizeOptions?: number[];
};

const DEFAULT_QUERY_OPTIONS: ListVoucherLogsQuery = {
  page: 0,
  limit: 20,
  sortBy: 'created_at',
  sortByDirection: 'desc',
};

const SORT_FIELD_TO_API_SORT_BY: Partial<Record<string, NonNullable<ListVoucherLogsQuery['sortBy']>>> = {
  createdAt: 'created_at',
  amount: 'amount',
  fkOrder: 'id_order',
};

const API_SORT_BY_TO_SORT_FIELD: Partial<Record<NonNullable<ListVoucherLogsQuery['sortBy']>, string>> = {
  created_at: 'createdAt',
  amount: 'amount',
  id_order: 'fkOrder',
};

const defaultSharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex' | 'editable' | 'filterable'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
  filterable: false,
  editable: false,
};

export default function VoucherLogsTable({
  voucherId,
  queryOptions,
  onQueryOptionsChange,
  onUserIdClick,
  onVoucherClick,
  pageSizeOptions = [20, 50, 100],
}: Props) {
  const [internalQueryOptions, setInternalQueryOptions] = useState<ListVoucherLogsQuery>(DEFAULT_QUERY_OPTIONS);
  const isControlled = queryOptions !== undefined;

  const resolvedQueryOptions = useMemo(() => {
    return {
      ...DEFAULT_QUERY_OPTIONS,
      ...(isControlled ? queryOptions : internalQueryOptions),
      ...(voucherId ? { voucherId } : {}),
    };
  }, [internalQueryOptions, isControlled, queryOptions, voucherId]);

  const updateQueryOptions = useCallback(
    (updater: (current: ListVoucherLogsQuery) => ListVoucherLogsQuery) => {
      const current = isControlled
        ? {
            ...DEFAULT_QUERY_OPTIONS,
            ...queryOptions,
          }
        : internalQueryOptions;
      const next = updater(current);

      if (!isControlled) {
        setInternalQueryOptions(next);
      }
      onQueryOptionsChange?.(next);
    },
    [internalQueryOptions, isControlled, onQueryOptionsChange, queryOptions],
  );

  const { data, isLoading } = useListVoucherLogs({
    query: resolvedQueryOptions,
  });

  const rows = data?.result?.logs ?? [];
  const total = Number(data?.result?.total ?? 0);

  const brand = useCurrentBrand();
  const uniqueUserIds = useMemo(() => {
    const ids = rows.flatMap((log) => [log.createdBy, log.userId]);
    return Array.from(new Set(ids.filter(Boolean))).sort();
  }, [rows]);
  const { data: usersSummaryMap } = useGetUsersSummaryByIds({ userIds: uniqueUserIds, brand });
  const resolvedUsersSummaryMap = usersSummaryMap ?? new Map();

  const uniqueVoucherIds = useMemo(() => {
    return Array.from(new Set(rows.map((log) => log.voucherId).filter(Boolean))).sort();
  }, [rows]);
  const { data: vouchersMap } = useGetVouchersByIds({ voucherIds: uniqueVoucherIds });
  const resolvedVouchersMap = vouchersMap ?? new Map();

  const columns = useMemo<Array<GridColDef<VoucherLog>>>(
    () => [
      {
        field: 'fkOrder',
        headerName: 'Order ID',
        minWidth: 60,
        ...defaultSharedAttrs,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.fkOrder}</Box>
            <Tooltip title="Open order">
              <IconButton
                component={Link}
                to={`/purchases/${params.row.fkOrder}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                size="small"
                sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { field: 'currencyCode', headerName: 'Currency', minWidth: 120, flex: 0.5, ...defaultSharedAttrs },
      {
        field: 'amount',
        headerName: 'Amount',
        minWidth: 130,
        flex: 0.5,
        ...defaultSharedAttrs,
        renderCell: (params) => (
          <>
            {currencyFormatter(params.row.metadata.voucherCurrency, params.row.amount)}{' '}
            {params?.row?.metadata?.remainingAmountAfter > 0
              ? `(${params?.row?.metadata?.remainingAmountAfter} left)`
              : ''}
          </>
        ),
      },
      {
        field: 'voucherId',
        headerName: 'Voucher',
        minWidth: 150,
        ...defaultSharedAttrs,
        renderCell: (params) => {
          const voucher = resolvedVouchersMap.get(params.row.voucherId);
          const label = voucher?.voucherName ?? params.row.voucherId;
          if (onVoucherClick && voucher?.voucherName) {
            return (
              <Button
                variant="text"
                size="small"
                onClick={() => onVoucherClick(voucher.voucherName)}
                sx={{ textTransform: 'none' }}
              >
                {label}
              </Button>
            );
          }
          return label;
        },
      },
      {
        field: 'userId',
        headerName: 'User',
        minWidth: 150,
        ...defaultSharedAttrs,
        renderCell: (params) => {
          const uid = params.row.userId;
          if (!uid) return '-';
          const userName = resolvedUsersSummaryMap.get(uid)?.full_name;
          if (onUserIdClick) {
            return (
              <Button variant="text" size="small" onClick={() => onUserIdClick(uid)} sx={{ textTransform: 'none' }}>
                {userName ?? uid}
              </Button>
            );
          }
          return <CopyableUserLink label={userName ?? uid} userId={uid} />;
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        minWidth: 180,
        ...defaultSharedAttrs,
        renderCell: (params) => DateFormatterDetailed({ date: params.value }),
      },
      {
        field: 'deletedAt',
        headerName: 'Deleted At',
        minWidth: 180,
        ...defaultSharedAttrs,
        renderCell: (params) => (params.value ? DateFormatterDetailed({ date: params.value }) : '-'),
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        minWidth: 200,
        ...defaultSharedAttrs,
        renderCell: (params) => {
          const userName = resolvedUsersSummaryMap.get(params.row.createdBy)?.full_name;
          return <CopyableUserLink label={userName ?? params.row.createdBy} userId={params.row.createdBy} />;
        },
      },
      {
        field: 'debug',
        headerName: 'Debug',
        minWidth: 120,
        ...defaultSharedAttrs,
        renderCell: (params) => <DebugModal type="generic" title="Log Row" data={params.row} />,
      },
      { field: 'voucherLogId', headerName: 'Log ID', minWidth: 260, flex: 1, ...defaultSharedAttrs },
      { field: 'voucherUserId', headerName: 'Voucher User ID', minWidth: 260, flex: 1, ...defaultSharedAttrs },
    ],
    [resolvedUsersSummaryMap, resolvedVouchersMap, onUserIdClick, onVoucherClick],
  );

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: resolvedQueryOptions.page ?? DEFAULT_QUERY_OPTIONS.page ?? 0,
      pageSize: resolvedQueryOptions.limit ?? DEFAULT_QUERY_OPTIONS.limit ?? 20,
    }),
    [resolvedQueryOptions.limit, resolvedQueryOptions.page],
  );

  const sortModel = useMemo<GridSortModel>(() => {
    if (!resolvedQueryOptions.sortBy) {
      return [];
    }
    const sortField = API_SORT_BY_TO_SORT_FIELD[resolvedQueryOptions.sortBy];
    if (!sortField) {
      return [];
    }

    return [
      {
        field: sortField,
        sort: (resolvedQueryOptions.sortByDirection ?? 'asc') as GridSortDirection,
      },
    ];
  }, [resolvedQueryOptions.sortBy, resolvedQueryOptions.sortByDirection]);

  const handlePaginationModelChange = useCallback(
    (nextModel: GridPaginationModel) => {
      updateQueryOptions((current) => ({
        ...current,
        page: nextModel.page,
        limit: nextModel.pageSize,
      }));
    },
    [updateQueryOptions],
  );

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      const nextSort = nextSortModel[0];
      if (!nextSort || !nextSort.sort) {
        updateQueryOptions((current) => ({
          ...current,
          sortBy: undefined,
          sortByDirection: undefined,
        }));
        return;
      }

      const nextSortBy = SORT_FIELD_TO_API_SORT_BY[nextSort.field];
      if (!nextSortBy) {
        return;
      }

      updateQueryOptions((current) => ({
        ...current,
        sortBy: nextSortBy,
        sortByDirection: nextSort.sort === 'desc' ? 'desc' : 'asc',
      }));
    },
    [updateQueryOptions],
  );

  const handleFilterChange = useCallback(
    (field: keyof ListVoucherLogsQuery, value: unknown) => {
      updateQueryOptions((current) => ({
        ...current,
        [field]: value || undefined,
        page: 0,
      }));
    },
    [updateQueryOptions],
  );

  return (
    <Box mt={3}>
      <PageSubheader title={`Voucher Logs (${total})`} />
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField
          label="User ID"
          size="small"
          value={resolvedQueryOptions.userId ?? ''}
          onChange={(e) => handleFilterChange('userId', e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <TextField
          label="Order ID"
          size="small"
          value={resolvedQueryOptions.orderId ?? ''}
          onChange={(e) => handleFilterChange('orderId', e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={resolvedQueryOptions.includeDeleted ?? false}
              onChange={(e) => handleFilterChange('includeDeleted', e.target.checked || undefined)}
            />
          }
          label="Include Deleted"
        />
      </Stack>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row) => row.voucherLogId}
        loading={isLoading}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        columnVisibilityModel={{
          currencyCode: false,
          deletedAt: !!resolvedQueryOptions.includeDeleted,
        }}
        pageSizeOptions={pageSizeOptions}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
